// LessonDirectionController.js — Task #351: Direction.auto, the derived performance
// -------------------------------------------------------------------------
// The POLICY layer of the direction grammar (THE BOARD GRAMMAR, Tier 1 item 5).
// Defaults-first: the runtime DERIVES the legend's gaze/gesture from board
// events — a reveal fires → turn/point at the board as it writes; a check
// opens → face the student; nothing happening → breathe with idle mannerisms.
// `beat.direction` (the closed ~12-verb authored slot) layers over these
// defaults for the ~5% of moments an author wants to direct.
//
// This class NEVER writes the rig. It only calls LessonReactionController's
// direct()/primitives, which enforce every sacred rule mechanically:
//   - A2F is KING of the FACE: during speech every gesture is head-BONE-only
//     (the sanctioned Task #258 ride); no face morph is ever written.
//   - No private rAF/timer cadence: tick(now) is stepped from the reaction
//     controller's update(), which ModelViewer.animate steps on the shared
//     facial loop.
//   - Clean interruption (Trap 2 — pause is a performance state): nothing is
//     ever frozen or hard-cut. Every envelope decays to zero on its own; on
//     hold/attentive this layer simply stops ISSUING teaching gestures and
//     switches to a calmer cadence — settle-to-idle, then an attentive loop.
//   - Rate caps: a global floor between derived gestures, a slower per-class
//     cap for board-directed moves, and jittered mannerism intervals so the
//     legend never flails, no matter how busy the cue stream is.
// Degradation: the stage only creates this layer on the standalone player
// (never preview / reduced-motion / the Lab shell), so every other surface —
// and any lesson with no direction data on a surface without this layer —
// behaves exactly as before.
// -------------------------------------------------------------------------

import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";

// Global floor between ANY two gestures this layer initiates.
const GLOBAL_MIN_GAP_MS = 2400;
// Board-directed defaults (reveal/mark class) are capped harder: the board can
// emit several cues per line, but the legend should turn to it far less often.
const BOARD_MIN_GAP_MS = 4500;
// Idle mannerism cadence (teaching/idle: the moments between board activity).
const IDLE_PULSE_MIN_MS = 7000;
const IDLE_PULSE_JITTER_MS = 4000;
// Attentive cadence (a check is open, or the clock is held): calmer, slower.
const ATTENTIVE_PULSE_MIN_MS = 9000;
const ATTENTIVE_PULSE_JITTER_MS = 5000;
// An authored "settle" suppresses derived gestures for this long.
const SETTLE_CALM_MS = 4000;

// Mannerism libraries — small closed rotations, deterministic order with a
// jittered interval so the rhythm breathes without ever repeating on a beat.
const IDLE_SEQ = ["consider", "tilt", "scan_board"];
const ATTENTIVE_SEQ = ["lean_in", "consider"];

export class LessonDirectionController {
    constructor(reaction) {
        this._reaction = reaction || null;
        this._enabled = !!reaction;
        this._mode = "teaching"; // teaching | attentive | idle
        this._held = false; // clock held (modal open) — Trap 2 performance state
        this._now = 0;
        this._lastGestureAt = -1e9;
        this._lastBoardAt = -1e9;
        this._calmUntil = 0;
        this._nextPulseAt = 0; // 0 = schedule lazily on first tick
        this._pulseCount = 0;
        this._revealCount = 0;
        this._deferredDrain = null;
    }

    /* ---------- shared-loop step (from LessonReactionController.update) ---------- */
    tick(now) {
        if (!this._enabled) return;
        this._now = now;
        // Authored/default ceremony work has priority over ambient pulses.
        // The stage retains rejected work, so a live gesture or rate floor can
        // never make the one-shot cue disappear.
        if (this._deferredDrain) {
            try { if (this._deferredDrain() === false) return; } catch (_) {}
        }
        if (!this._nextPulseAt) {
            // First tick: settle in before the first mannerism.
            this._schedulePulse(now);
            return;
        }
        if (now < this._nextPulseAt) return;
        // While the voice flows, the performance is driven by LIVE board cues
        // (onBoardActivity), not the idle loop — re-arm a full quiet interval
        // so mannerisms resume a natural beat AFTER the line ends, never 1s in.
        if (A2FLipSyncDriver.isAnyActive()) {
            this._schedulePulse(now);
            return;
        }
        if (now < this._calmUntil) return;
        if (now - this._lastGestureAt < GLOBAL_MIN_GAP_MS) return;
        const attentive = this._held || this._mode === "attentive";
        const seq = attentive ? ATTENTIVE_SEQ : IDLE_SEQ;
        const verb = seq[this._pulseCount % seq.length];
        this._pulseCount += 1;
        // direct() may refuse (mid-gesture / its own floor) — that's the cap
        // working. Only bookkeep when a gesture actually started.
        if (this._reaction && this._reaction.direct(verb)) {
            this._lastGestureAt = now;
        }
        this._schedulePulse(now);
    }

    _schedulePulse(now) {
        const attentive = this._held || this._mode === "attentive";
        const base = attentive ? ATTENTIVE_PULSE_MIN_MS : IDLE_PULSE_MIN_MS;
        const jitter = attentive ? ATTENTIVE_PULSE_JITTER_MS : IDLE_PULSE_JITTER_MS;
        this._nextPulseAt = now + base + Math.random() * jitter;
    }

    _pnow() {
        return this._now || (typeof performance !== "undefined"
            ? performance.now() : Date.now());
    }

    /* ---------- stage lifecycle ---------- */

    /** teaching | attentive | idle. Attentive = an interaction is open (check,
     * problem, game): the legend faces the student and breathes calmly. Mode
     * changes never cut a running gesture — envelopes decay on their own. */
    setMode(mode) {
        if (mode !== "teaching" && mode !== "attentive" && mode !== "idle") return;
        if (this._mode === mode) return;
        this._mode = mode;
        this._schedulePulse(this._pnow());
    }

    /** Trap 2 — pause is a performance state. While the clock is held (modal
     * up, lesson paused) the legend settles to idle and runs the attentive
     * loop; he can never freeze mid-gesture because nothing here (or in the
     * reaction controller) ever halts an envelope — we only stop STARTING
     * teaching gestures and slow the cadence. */
    setHeld(held) {
        const h = !!held;
        if (this._held === h) return;
        this._held = h;
        this._schedulePulse(this._pnow());
    }

    /* ---------- derived defaults (the 95%) ---------- */

    /** A live board event fired ("reveal" | "mark"): turn toward the board.
     * Reveals alternate point/gaze so repetition never reads robotic; marks
     * get a quick glance. Hard-capped per class + globally. */
    onBoardActivity(type) {
        if (!this._enabled) return;
        const now = this._pnow();
        if (now < this._calmUntil) return;
        if (now - this._lastBoardAt < BOARD_MIN_GAP_MS) return;
        if (now - this._lastGestureAt < GLOBAL_MIN_GAP_MS) return;
        let verb;
        if (type === "mark") {
            verb = "glance_board";
        } else {
            verb = this._revealCount % 3 === 0 ? "point_board" : "gaze_board";
            this._revealCount += 1;
        }
        if (this._reaction && this._reaction.direct(verb)) {
            this._lastGestureAt = now;
            this._lastBoardAt = now;
            // A fresh board gesture resets the mannerism clock.
            this._schedulePulse(now);
        }
    }

    /** An interaction opened (checkpoint / problem / game): face the student.
     * A strong moment — bypasses the board-class cap; direct()'s own floor
     * still applies, and a live gesture is never cut. */
    onCheckOpened() {
        if (!this._enabled) return;
        this.setMode("attentive");
        const now = this._pnow();
        if (now < this._calmUntil) return;
        if (this._reaction && this._reaction.direct("gaze_student")) {
            this._lastGestureAt = now;
            this._schedulePulse(now);
        }
    }

    /* ---------- authored overrides (the 5%) ---------- */

    /** An authored `beat.direction` verb fired on its narration anchor. The
     * author chose this moment, so it bypasses the derived class caps —
     * direct() still floors the rate and never cuts a live gesture, so even
     * dense authoring cannot make the legend flail. */
    onAuthoredVerb(verb, context) {
        if (!this._enabled) return false;
        const now = this._pnow();
        if (verb === "settle") {
            this._calmUntil = now + SETTLE_CALM_MS;
            this._nextPulseAt = Math.max(this._nextPulseAt, this._calmUntil);
            if (this._reaction) this._reaction.direct("settle", context);
            return true;
        }
        if (this._reaction && this._reaction.direct(verb, context)) {
            this._lastGestureAt = now;
            this._schedulePulse(now);
            return true;
        }
        return false;
    }

    setDeferredDrain(fn) {
        this._deferredDrain = typeof fn === "function" ? fn : null;
    }

    dispose() {
        this._enabled = false;
        this._deferredDrain = null;
        this._reaction?.cancel?.();
        if (this._reaction && this._reaction.director === this) {
            this._reaction.director = null;
        }
        this._reaction = null;
    }
}

export default LessonDirectionController;
